import { useState } from "react";
import { useProjects } from "../lib/ProjectContext";
import { api } from "../lib/apiClient";
import { SOURCE_TYPE_HIERARCHY, type SourceType, type DocumentRecord } from "@shared/types";

// Mirrors MAX_HTTP_UPLOAD_BYTES / MAX_CHUNKED_UPLOAD_BYTES in
// netlify/functions/_shared/validation.ts (not imported — this file isn't
// bundled for the Functions runtime, same reason the old single value was
// already duplicated here rather than shared). CHUNK_SIZE_BYTES is also the
// per-request ceiling: anything larger uploads as a sequence of pieces this
// size instead of one request.
const CHUNK_SIZE_BYTES = 4 * 1024 * 1024;
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

export const SOURCE_TYPE_LABEL: Record<SourceType, string> = {
  contract_plans: "Contract Plans",
  special_provisions: "Special Provisions",
  construction_control_directive: "Construction Control Directive (CCD)",
  standard_specification: "ODOT Standard Specification",
  design_standard: "Design Standard",
  materials_testing: "Materials/Testing Standard",
  veteran_knowledge: "Veteran Knowledge",
  other: "Other",
};

function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function AdminUploadPage() {
  const { selectedProjectId } = useProjects();
  const [title, setTitle] = useState("");
  const [sourceType, setSourceType] = useState<SourceType>("standard_specification");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DocumentRecord | null>(null);
  const [chunkProgress, setChunkProgress] = useState<{ done: number; total: number } | null>(null);

  const [vkTitle, setVkTitle] = useState("");
  const [vkText, setVkText] = useState("");
  const [vkScope, setVkScope] = useState<"project" | "shared">("project");
  const [vkSubmitting, setVkSubmitting] = useState(false);
  const [vkError, setVkError] = useState<string | null>(null);
  const [vkResult, setVkResult] = useState<DocumentRecord | null>(null);

  async function uploadDirect(file: File): Promise<DocumentRecord> {
    const fileContentBase64 = await fileToBase64(file);
    const data = await api.post<{ document: DocumentRecord }>(`/api/projects/${selectedProjectId}/documents`, {
      title,
      sourceType,
      originalFilename: file.name,
      mimeType: file.type,
      fileContentBase64,
    });
    return data.document;
  }

  /**
   * Files over CHUNK_SIZE_BYTES go up as a sequence of smaller requests
   * (each within the single-request payload ceiling) instead of one big
   * one, then get assembled server-side — see documents-upload-chunk.ts /
   * documents-upload-finalize.ts. Sequential, not parallel: this is an
   * owner doing a one-off upload, not a throughput-critical path, and
   * sequential keeps the progress count and error attribution simple.
   */
  async function uploadChunked(file: File): Promise<DocumentRecord> {
    const uploadId = crypto.randomUUID();
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE_BYTES);
    setChunkProgress({ done: 0, total: totalChunks });

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE_BYTES;
      const chunk = file.slice(start, Math.min(start + CHUNK_SIZE_BYTES, file.size));
      const dataBase64 = await fileToBase64(chunk);
      try {
        await api.post(`/api/projects/${selectedProjectId}/documents/upload-chunk`, { uploadId, chunkIndex: i, dataBase64 });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed.";
        throw new Error(`Upload failed on piece ${i + 1} of ${totalChunks}: ${message}`);
      }
      setChunkProgress({ done: i + 1, total: totalChunks });
    }

    await api.post(`/api/projects/${selectedProjectId}/documents/upload-finalize`, {
      uploadId,
      totalChunks,
      title,
      sourceType,
      originalFilename: file.name,
      mimeType: file.type,
    });

    // Assembly runs in a background function (it outlasts a normal request),
    // so poll for the outcome instead of waiting on the finalize response.
    const POLL_INTERVAL_MS = 3000;
    const POLL_TIMEOUT_MS = 15 * 60 * 1000;
    const startedAt = Date.now();
    while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      let status: { state: string; document?: DocumentRecord; message?: string };
      try {
        status = await api.get(`/api/projects/${selectedProjectId}/documents/upload-status?uploadId=${uploadId}`);
      } catch {
        continue; // transient (or status not written yet) — keep polling
      }
      if (status.state === "done" && status.document) return status.document;
      if (status.state === "error") throw new Error(status.message ?? "Upload processing failed.");
    }
    throw new Error("Upload is still processing after 15 minutes. Check the Sources screen before retrying.");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProjectId || !file) return;
    setError(null);
    setResult(null);

    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`That file is ${(file.size / 1024 / 1024).toFixed(1)}MB — over the ${MAX_UPLOAD_BYTES / 1024 / 1024}MB upload limit.`);
      return;
    }

    setSubmitting(true);
    try {
      const document = file.size <= CHUNK_SIZE_BYTES ? await uploadDirect(file) : await uploadChunked(file);
      setResult(document);
      setTitle("");
      setFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setSubmitting(false);
      setChunkProgress(null);
    }
  }

  async function handleVeteranKnowledgeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProjectId || !vkTitle.trim() || !vkText.trim()) return;
    setVkError(null);
    setVkResult(null);
    setVkSubmitting(true);
    try {
      const data = await api.post<{ document: DocumentRecord }>(`/api/projects/${selectedProjectId}/veteran-knowledge`, {
        title: vkTitle,
        text: vkText,
        scope: vkScope,
      });
      setVkResult(data.document);
      setVkTitle("");
      setVkText("");
    } catch (err) {
      setVkError(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setVkSubmitting(false);
    }
  }

  return (
    <div className="content-col">
      <h2 style={{ marginTop: 0 }}>Upload a source document</h2>
      <p className="muted">
        PDF, DOCX, or an image (PNG/JPEG/WEBP), up to {MAX_UPLOAD_BYTES / 1024 / 1024}MB — larger files upload automatically in pieces, no extra
        steps needed. New documents start in <strong>Draft</strong> and won't be used to answer questions until you
        approve them on the Sources screen.
      </p>
      <form className="card" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="title">Title</label>
          <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="sourceType">Source type</label>
          <select id="sourceType" value={sourceType} onChange={(e) => setSourceType(e.target.value as SourceType)}>
            {SOURCE_TYPE_HIERARCHY.filter((t) => t !== "veteran_knowledge").map((t) => (
              <option key={t} value={t}>
                {SOURCE_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="file">File</label>
          <input
            id="file"
            type="file"
            accept=".pdf,.docx,.png,.jpg,.jpeg,.webp"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            required
          />
        </div>
        <button className="btn btn-primary" type="submit" disabled={submitting || !file || !title}>
          {chunkProgress ? `Uploading… (${chunkProgress.done}/${chunkProgress.total})` : submitting ? "Uploading…" : "Upload"}
        </button>
        {error && <p className="error-text" style={{ marginTop: "0.5rem" }}>{error}</p>}
      </form>

      {result && (
        <div className="card">
          <p>
            <strong>{result.title}</strong> uploaded (v{result.version}). Ingest status: {result.ingestStatus}.
          </p>
          <p className="muted">Refresh the Sources screen in a moment to approve it once ingestion finishes.</p>
        </div>
      )}

      <h2>Add veteran / field knowledge</h2>
      <p className="muted">
        Type up what an experienced inspector or engineer knows from the field — the "why" behind a technique, a
        workaround that held up on a past project, a judgment call the spec doesn't spell out. It's treated as{" "}
        <strong>guidance only</strong>: the assistant can offer it alongside an answer but will never let it override
        a contract document, CCD, or spec section — see the Ask page's answer for how conflicts get flagged. Mark it{" "}
        <strong>shared</strong> if the lesson could apply beyond this one project, or keep it scoped to this project
        if it's specific to this job's conditions.
      </p>
      <form className="card" onSubmit={handleVeteranKnowledgeSubmit}>
        <div className="field">
          <label htmlFor="vkTitle">Title</label>
          <input
            id="vkTitle"
            type="text"
            value={vkTitle}
            onChange={(e) => setVkTitle(e.target.value)}
            placeholder='e.g. "Cold-weather concrete pours below 40°F"'
            required
          />
        </div>
        <div className="field">
          <label htmlFor="vkScope">Applies to</label>
          <select id="vkScope" value={vkScope} onChange={(e) => setVkScope(e.target.value as "project" | "shared")}>
            <option value="project">This project only</option>
            <option value="shared">Shared — may apply across projects</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="vkText">Knowledge</label>
          <textarea
            id="vkText"
            value={vkText}
            onChange={(e) => setVkText(e.target.value)}
            rows={8}
            placeholder="Write it up like you're explaining it to the next inspector who runs into this — what happened, what you did, and why it worked."
            required
          />
        </div>
        <button className="btn btn-primary" type="submit" disabled={vkSubmitting || !vkTitle.trim() || !vkText.trim()}>
          {vkSubmitting ? "Saving…" : "Save"}
        </button>
        {vkError && <p className="error-text" style={{ marginTop: "0.5rem" }}>{vkError}</p>}
      </form>

      {vkResult && (
        <div className="card">
          <p>
            <strong>{vkResult.title}</strong> saved. Ingest status: {vkResult.ingestStatus}.
          </p>
          <p className="muted">Refresh the Sources screen in a moment to approve it once ingestion finishes.</p>
        </div>
      )}
    </div>
  );
}
